import { createLogger } from './logger';
import { isTokenExpired } from './jwt';

const log = createLogger('api');

export const API_BASE = import.meta.env.VITE_API_BASE || '';
const TOKEN_STORAGE_KEY = 'idp_token';

/**
 * Window event dispatched whenever we detect that the current session is no
 * longer valid (token expired locally, or the API rejected us with 401/403).
 *
 * `AuthProvider` listens for this and triggers a logout, which sends the user
 * back to the login screen instead of leaving the UI in a stuck "connected"
 * state with stale data — this is what used to require a manual logout +
 * Ctrl-Shift-R after the tab sat overnight.
 */
export const AUTH_EXPIRED_EVENT = 'auth:expired';

export interface AuthExpiredDetail {
  reason: string;
}

function dispatchAuthExpired(reason: string): void {
  log.info(`Session invalid (${reason}); requesting logout`);
  window.dispatchEvent(
    new CustomEvent<AuthExpiredDetail>(AUTH_EXPIRED_EVENT, {
      detail: { reason },
    })
  );
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export class SessionExpiredError extends Error {
  constructor(reason: string) {
    super(`Session expired (${reason}). Please sign in again.`);
    this.name = 'SessionExpiredError';
  }
}

/**
 * Centralized fetch wrapper for all backend API calls.
 *
 *  - Prepends `API_BASE` for relative paths (callers can also pass full URLs).
 *  - Attaches `Authorization: Bearer <idp_token>` when present.
 *  - Refuses to send a request whose token is already expired and signals the
 *    auth provider to log the user out instead.
 *  - On 401/403 from the backend, fires the same logout signal so the UI
 *    immediately reflects the invalid session.
 */
export async function apiFetch(
  input: string,
  init: RequestInit = {}
): Promise<Response> {
  const token = getStoredToken();

  if (token && isTokenExpired(token)) {
    dispatchAuthExpired('token-expired-preflight');
    throw new SessionExpiredError('token-expired-preflight');
  }

  const headers = new Headers(init.headers);
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const url = /^https?:\/\//i.test(input) ? input : `${API_BASE}${input}`;
  const response = await fetch(url, { ...init, headers });

  if (response.status === 401 || response.status === 403) {
    dispatchAuthExpired(`http-${response.status}`);
  }

  return response;
}
